import type { CallOutcome, ProspectStatus } from "../../types/prospects";

export const STATUS_LABEL: Record<ProspectStatus, string> = {
	new: "New",
	to_call: "To call",
	callback: "Call back",
	interested: "Interested",
	not_interested: "Not interested",
	signed_up: "Signed up",
	do_not_contact: "Do not contact",
	parked: "Parked",
};

export const STATUS_DOT: Record<string, string> = {
	new: "bg-zinc-300",
	to_call: "bg-sky-500",
	callback: "bg-amber-400",
	interested: "bg-emerald-500",
	not_interested: "bg-rose-500",
	signed_up: "bg-green-700",
	do_not_contact: "bg-zinc-800",
	parked: "bg-zinc-200",
	running: "bg-sky-500",
	done: "bg-emerald-500",
	failed: "bg-red-500",
	interrupted: "bg-zinc-400",
};

export const OUTCOME_LABEL: Record<CallOutcome, string> = {
	no_answer: "No answer",
	callback: "Call back",
	interested: "Interested",
	not_interested: "Not interested",
	wrong_person: "Wrong person",
	signed_up: "Signed up",
	do_not_contact: "Do not contact",
};

export const OUTCOME_COLOR: Record<string, string> = {
	no_answer: "#a1a1aa",
	callback: "#fbbf24",
	interested: "#10b981",
	not_interested: "#f43f5e",
	wrong_person: "#60a5fa",
	signed_up: "#15803d",
	do_not_contact: "#27272a",
	unknown: "#d4d4d8",
};

export const PLATFORM_LABEL: Record<string, string> = {
	shopify: "Shopify",
	dandomain_webshop: "DanDomain Webshop",
	dandomain: "DanDomain",
	shoporama: "Shoporama",
	wannafind: "Wannafind",
	scannet: "Scannet",
	woocommerce: "WooCommerce",
	magento: "Magento",
	prestashop: "PrestaShop",
	shopware: "Shopware",
	bigcommerce: "BigCommerce",
	wix: "Wix",
	squarespace: "Squarespace",
	centra: "Centra",
};

export const NETWORK_LABEL: Record<string, string> = {
	partner_ads: "Partner-ads",
	adtraction: "Adtraction",
};

export const RESEARCH_LABEL: Record<string, string> = {
	pending: "Not researched",
	no_domain: "Website needed",
	unreachable: "Site unreachable",
	probed: "Homepage only",
	sampled: "Researched",
};

export const StatusDot = ({
	status,
	label,
}: {
	status: string;
	label?: string;
}) => (
	<span className="inline-flex items-center gap-1.5 whitespace-nowrap">
		<span
			className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[status] ?? "bg-zinc-300"}`}
		/>
		{label ?? STATUS_LABEL[status as ProspectStatus] ?? status}
	</span>
);

export const compact = (n: number | null | undefined) =>
	n === null || n === undefined
		? "—"
		: new Intl.NumberFormat("da-DK", {
				notation: n >= 10_000 ? "compact" : "standard",
				maximumFractionDigits: 1,
			}).format(n);

export const nextActionLabel = (iso: string | null) => {
	if (!iso) return null;
	const at = new Date(iso);
	const now = new Date();
	const sameDay = at.toDateString() === now.toDateString();
	const time = at.toLocaleTimeString("da-DK", {
		hour: "2-digit",
		minute: "2-digit",
	});
	if (at.getTime() < now.getTime() && !sameDay)
		return { text: `Overdue · ${at.toLocaleDateString("da-DK")}`, late: true };
	if (sameDay) return { text: `Today ${time}`, late: at < now };
	return {
		text: `${at.toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short" })} ${time}`,
		late: false,
	};
};
